import {
  Network,
  Sparkles,
  ScrollText,
  Languages,
  Command,
  Gauge,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { t, useLang, type Lang, type StringTable } from '@/lib/i18n';

type FeatureKey =
  | 'graph'
  | 'synthesis'
  | 'evidence'
  | 'bilingual'
  | 'palette'
  | 'confidence';

const STRINGS = {
  'features.eyebrow': {
    en: 'What separates an archive from a chatbot',
    fa: 'تفاوت یک آرشیو از یک گفتگوگر',
  },
  'features.title.lead': {
    en: 'Built for analysts who must',
    fa: 'ساخته‌شده برای تحلیل‌گرانی که باید',
  },
  'features.title.accent': { en: 'show their work', fa: 'کارشان را نشان دهند' },
  'features.title.tail': { en: '.', fa: '.' },
  'features.subtitle': {
    en: 'Six load-bearing decisions, each chosen against the alternative of a polished general-purpose assistant. None are negotiable for the work this archive supports.',
    fa: 'شش تصمیم بنیادین، هر یک در برابر گزینهٔ یک دستیار همه‌منظورهٔ صیقل‌خورده برگزیده شده‌اند. هیچ‌کدام برای کاری که این آرشیو پشتیبانی می‌کند قابل مذاکره نیستند.',
  },
} satisfies StringTable<string>;

type Feature = {
  key: FeatureKey;
  icon: LucideIcon;
  label: { en: string; fa: string };
  title: { en: string; fa: string };
  description: { en: string; fa: string };
};

const features: Feature[] = [
  {
    key: 'graph',
    icon: Network,
    label: {
      en: 'Graph-native retrieval',
      fa: 'بازیابی مبتنی بر گراف',
    },
    title: {
      en: 'A relational map, not a keyword index.',
      fa: 'یک نقشهٔ رابطه‌ای، نه نمایه‌ای از کلیدواژه.',
    },
    description: {
      en: 'Persons, organizations, instruments, and events are first-class nodes with typed edges. Queries traverse meaning — neighborhoods, paths, time slices — instead of matching strings.',
      fa: 'افراد، نهادها، اسناد و رویدادها گره‌های درجهٔ اول با یال‌های نوع‌دارند. پرس‌وجو در مسیر معنا حرکت می‌کند — همسایگی، مسیر، برشِ زمان — نه در تطابقِ کلمه.',
    },
  },
  {
    key: 'synthesis',
    icon: Sparkles,
    label: { en: 'Streaming synthesis', fa: 'هم‌نهاد سازیِ جاری' },
    title: {
      en: 'Read the answer as the model writes it.',
      fa: 'پاسخ را همان‌گونه که نوشته می‌شود بخوانید.',
    },
    description: {
      en: 'Natural-language input streams over SSE — reasoning, then extracted entities, then retrieved facts, then the answer itself, token by token. Stop the moment it goes off course.',
      fa: 'ورودیِ طبیعی به‌صورت SSE جاری می‌شود — استدلال، سپس موجودیت‌های استخراج‌شده، سپس واقعیت‌های بازیابی‌شده، سپس خودِ پاسخ، نشانه به نشانه. می‌توانید لحظه‌ای متوقفش کنید.',
    },
  },
  {
    key: 'evidence',
    icon: ScrollText,
    label: { en: 'Provenance over polish', fa: 'سندیّت بر زیبایی' },
    title: {
      en: 'Every claim links to its source.',
      fa: 'هر ادعا به منبع خود پیوند دارد.',
    },
    description: {
      en: 'No orphan assertions. Each sentence is anchored to the document, passage, or graph node it came from. Inline citations open the original passage. Defensible by construction.',
      fa: 'هیچ ادعایی بدون ریشه نیست. هر جمله به سند، گزاره یا گرهی که از آن آمده لنگر دارد. ارجاع‌های درون‌متنی، متنِ اصلی را باز می‌کنند. قابل دفاع به‌حکمِ ساختار.',
    },
  },
  {
    key: 'confidence',
    icon: Gauge,
    label: { en: 'Calibrated confidence', fa: 'اطمینان درجه‌بندی‌شده' },
    title: {
      en: 'Every section carries its own score.',
      fa: 'هر بخش، نمرهٔ اطمینانِ خود را دارد.',
    },
    description: {
      en: 'Synthesis is broken into sections, each with an explicit confidence value. Anything below 0.5 surfaces a "Limited evidence" warning. We never render generated content without a meter beside it.',
      fa: 'هم‌نهادسازی به بخش‌هایی تقسیم می‌شود که هر یک مقدار اطمینانِ آشکار دارد. هرچه زیر ۰٫۵ باشد، هشدارِ «شواهد محدود» نمایش می‌یابد. هیچ محتوای تولیدی بدون نمایش‌گرِ کنارش ارائه نمی‌شود.',
    },
  },
  {
    key: 'bilingual',
    icon: Languages,
    label: {
      en: 'Bilingual by architecture',
      fa: 'دوزبانه در معماری',
    },
    title: {
      en: 'Persian and English, end to end.',
      fa: 'فارسی و انگلیسی، از آغاز تا پایان.',
    },
    description: {
      en: 'Every entity carries name and nameFa. Every component flips on lang and dir. Logical CSS properties throughout. Persian is not a translation pass — it is a first-class direction of the system.',
      fa: 'هر موجودیت نامِ لاتین و نامِ فارسی دارد. هر کامپوننت با lang و dir می‌چرخد. خصوصیات منطقیِ CSS در همه‌جا. فارسی، گذرگاهِ ترجمه نیست — یک جهتِ درجهٔ اولِ این سامانه است.',
    },
  },
  {
    key: 'palette',
    icon: Command,
    label: { en: 'Keyboard-first', fa: 'پیش‌گامی صفحه‌کلید' },
    title: {
      en: 'Command palette with three routing modes.',
      fa: 'پنل فرمان با سه حالتِ مسیریابی.',
    },
    description: {
      en: 'Open ⌘K. Search entities, jump between dossiers, or pose a question to the model — the same surface routes to the right destination. Built for the analyst who never reaches for a mouse.',
      fa: 'با ⌘K باز می‌شود. جست‌وجوی موجودیت‌ها، پرش میان پرونده‌ها، یا طرح پرسش از مدل — همین سطح، شما را به مقصد درست هدایت می‌کند. برای تحلیل‌گری ساخته شده که هیچ‌گاه به ماوس دست نمی‌زند.',
    },
  },
];

export function Features() {
  const lang = useLang();
  const pick = (s: { en: string; fa: string }, l: Lang) => s[l];

  return (
    <section
      id="features"
      className="border-t border-[var(--color-border)] bg-[var(--color-canvas)]"
    >
      <div className="mx-auto max-w-[88rem] px-6 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="mb-4 text-xs uppercase tracking-wider text-[var(--color-ink-muted)]">
            {t(STRINGS, 'features.eyebrow', lang)}
          </div>
          <h2 className="text-3xl leading-tight md:text-5xl">
            {t(STRINGS, 'features.title.lead', lang)}{' '}
            <span className="text-[var(--color-accent)]">
              {t(STRINGS, 'features.title.accent', lang)}
            </span>
            {t(STRINGS, 'features.title.tail', lang)}
          </h2>
          <p className="mt-4 text-[var(--color-ink-secondary)] md:text-lg">
            {t(STRINGS, 'features.subtitle', lang)}
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {features.map(({ key, icon: Icon, label, title, description }) => (
            <article
              key={key}
              className="glass-card group flex flex-col rounded-md p-6 transition-colors hover:border-[color-mix(in_oklab,var(--color-accent)_55%,transparent)]"
            >
              <div className="mb-4 inline-flex size-9 items-center justify-center rounded-sm border border-[color-mix(in_oklab,var(--color-accent)_32%,transparent)] bg-[color-mix(in_oklab,var(--color-accent)_14%,transparent)] text-[var(--color-accent-bright)] shadow-[inset_0_1px_0_color-mix(in_oklab,#fff_8%,transparent)]">
                <Icon className="size-4" />
              </div>
              <div className="mb-2 text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
                {pick(label, lang)}
              </div>
              <h3 className="text-xl leading-snug">{pick(title, lang)}</h3>
              <p className="mt-2 text-sm text-[var(--color-ink-secondary)]">
                {pick(description, lang)}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
